import { mergeTheme, nocturne, type ThemeVariant } from '@ce/theme';
import type { TenantBundle } from '@ce/core';
import { divider, kicker, t } from '../styles';

/**
 * Appearance picker.
 *
 * The swatch on each card is rendered from that variant's own resolved tokens,
 * not from a hand-written preview colour — so a card cannot drift from what
 * choosing it actually does, and a client adding a variant in the CMS gets a
 * correct preview for free.
 */
export function ThemePicker({
  bundle,
  active,
  shuffle,
  onPick,
  onToggleShuffle,
}: {
  bundle: TenantBundle;
  active: string | null;
  shuffle: boolean;
  onPick: (id: string) => void;
  onToggleShuffle: () => void;
}) {
  const variants = bundle.themeVariants ?? [];
  // One look means no choice to make. Don't show a picker with a single row.
  if (variants.length < 2) return null;

  const base = mergeTheme(nocturne, bundle.theme);

  return (
    <>
      <div style={{ ...kicker(), margin: '24px 0 5px' }}>{bundle.strings.themePickerTitle}</div>
      <div style={{ fontSize: 11.5, lineHeight: 1.5, color: t(0.45), marginBottom: 11 }}>
        {bundle.strings.themePickerNote}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {variants.map((variant) => {
          const resolved = mergeTheme(base, variant.patch);
          const on = active === variant.id;
          return (
            <button
              key={variant.id}
              type="button"
              onClick={() => onPick(variant.id)}
              aria-pressed={on}
              style={{
                display: 'flex', alignItems: 'center', gap: 13, width: '100%',
                textAlign: 'left', padding: '11px 13px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                background: on ? 'rgba(var(--color-accent-rgb), 0.14)' : 'transparent',
                color: 'var(--color-text)',
              }}
            >
              <Swatch variant={variant} resolved={resolved} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '500 14.5px/1.2 var(--font-body)' }}>{variant.label}</div>
                <div style={{ fontSize: 11.5, lineHeight: 1.45, color: t(0.5), marginTop: 4 }}>
                  {variant.note}
                </div>
              </div>

              <div
                style={{
                  flex: 'none', width: 20, height: 20, borderRadius: '50%',
                  border: `1.5px solid ${on ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.25)'}`,
                  background: on ? 'var(--color-accent)' : 'transparent',
                  boxShadow: on ? 'inset 0 0 0 4px var(--color-surface)' : 'none',
                }}
              />
            </button>
          );
        })}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={shuffle}
        onClick={onToggleShuffle}
        style={{
          display: 'flex', alignItems: 'center', gap: 13, width: '100%', textAlign: 'left',
          marginTop: 12, padding: '11px 0', border: 0, borderTop: `1px solid ${divider}`,
          background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: '500 13.5px/1.3 var(--font-body)' }}>
            {bundle.strings.themeShuffleLabel}
          </div>
          <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 4 }}>
            {bundle.strings.themeShuffleNote}
          </div>
        </div>
        <div
          style={{
            flex: 'none', width: 38, height: 22, borderRadius: 11, padding: 2,
            display: 'flex', justifyContent: shuffle ? 'flex-end' : 'flex-start',
            border: `1px solid ${shuffle ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.2)'}`,
            background: shuffle ? 'rgba(var(--color-accent-rgb), 0.24)' : 'transparent',
            transition: 'all .18s ease',
          }}
        >
          <div
            style={{
              width: 16, height: 16, borderRadius: '50%',
              background: shuffle ? 'var(--color-accent-400)' : 'rgba(var(--color-text-rgb), 0.35)',
            }}
          />
        </div>
      </button>
    </>
  );
}

/**
 * A miniature of the app in that theme: its ground, a surface card, its arc
 * motif, and its accent — enough to read the *feel* rather than just the hue.
 * Corner radius comes from the variant too, since that is half of what makes
 * Halogen feel technical and Verdant feel soft.
 */
function Swatch({ variant, resolved }: { variant: ThemeVariant; resolved: ReturnType<typeof mergeTheme> }) {
  const c = resolved.colors;
  return (
    <div
      aria-hidden
      style={{
        flex: 'none', width: 52, height: 52, borderRadius: resolved.radius.md,
        background: c.bg, position: 'relative', overflow: 'hidden',
        boxShadow: `0 0 0 1px ${c.neutral[800]}`,
      }}
    >
      <div
        style={{
          position: 'absolute', top: -14, right: -14, width: 40, height: 40,
          borderRadius: '50%', border: `1px solid ${c.accent}`, opacity: 0.55,
        }}
      />
      <div
        style={{
          position: 'absolute', left: 7, right: 7, bottom: 7, height: 20,
          borderRadius: Math.max(2, resolved.radius.sm), background: c.surface,
          display: 'flex', alignItems: 'center', gap: 4, padding: '0 5px',
        }}
      >
        <div style={{ width: 2, height: 11, borderRadius: 2, background: c.accent }} />
        <div style={{ flex: 1, height: 2, borderRadius: 2, background: c.text, opacity: 0.55 }} />
      </div>
      <div
        style={{
          position: 'absolute', left: 7, top: 8, width: 22, height: 3,
          borderRadius: 2, background: c.accent,
        }}
      />
      <span style={{ display: 'none' }}>{variant.id}</span>
    </div>
  );
}
